/**
 * isDevice device-mode unlock dialog. Three modes share this component:
 *   • `unlock`      — admin in fullscreen-register hits the menu icon.
 *                     Successful auth removes `isDeviceMode` and reveals the
 *                     sidebar.
 *   • `lock-screen` — admin/manager/server hit the sidebar "Lock" button. The
 *                     register goes fullscreen; this dialog re-prompts on top
 *                     of it, and a successful auth clears the lock + restores
 *                     the sidebar.
 *   • `time-clock`  — staff member taps "Start Shift" / "End Shift" in the
 *                     cashier 3-dots menu. Same employee-picker UI; the
 *                     credential field accepts either a 4-digit clock-in PIN
 *                     OR the account password. Submit calls /api/timeclock/
 *                     clockIn or clockOut directly — no separate
 *                     /authenticate hop, since the time-clock endpoints
 *                     verify the credential server-side and atomically write
 *                     the punch event.
 *
 * All modes accept the current user OR any other staff member — the dropdown
 * lists employees by name. We authenticate against `/users/authenticate`
 * (unlock/lock-screen) or `/timeclock/clock{In,Out}` (time-clock), both keyed
 * off `username`, NOT email — the prior dialog passed `email`, which only
 * worked when the user happened to log in with their email as their username.
 */
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AuthenticationService} from '../../../_services/authentication.service';
import {UserService} from '../../../_services/user.service';
import {UtilsService} from '../../../_services/utils.service';
import {Observable, Subscription} from 'rxjs';
import {
  ClockEventService,
  ClockInResponse,
  ClockOutResponse,
  PreviewPunchResponse,
  ClockEvent,
  ClockShift,
} from '../../../_services/clockEvent.service';
import {User} from '../../../_models';
import {Constants} from '../../../constants/constants';
import {BarcodeScanService} from '../../../_services/barcode-scan.service';
import {NetworkPrinterService} from '../../../_services/networkPrinter.service';

interface UnlockEmployee {
  username: string;
  displayName: string;
  role: string;
}

type Mode = 'unlock' | 'lock-screen' | 'time-clock';
type ClockAction = 'clockIn' | 'clockOut';

@Component({
  selector: 'app-device-mode-unlock-popup',
  templateUrl: './device-mode-unlock-popup.component.html',
  styleUrls: ['./device-mode-unlock-popup.component.scss'],
})
export class DeviceModeUnlockPopupComponent implements OnInit, OnDestroy {
  mode: Mode = 'unlock';
  clockAction: ClockAction = 'clockIn';
  // The action the cashier originally requested from the menu (Start Shift
  // → 'clockIn' / End Shift → 'clockOut'). Preserved so we can show an
  // intent-mismatch banner on the confirm step if `clockAction` ends up
  // different because the selected employee's actual shift state didn't
  // match what the menu showed. NEVER mutated after construction.
  requestedAction: ClockAction = 'clockIn';
  selectedUsername = '';
  password = '';
  errorMessage = '';
  loading = false;
  loadingEmployees = false;
  employees: UnlockEmployee[] = [];

  /**
   * time-clock 2-step flow. After PIN entry the dialog flips into a
   * confirmation screen showing the employee's name, current open-shift
   * status, and today's punches. The cashier must press the explicit
   * Clock In/Out button to commit — Cancel closes without writing.
   */
  confirmStep = false;
  previewName = '';
  previewOpenShift: ClockShift | null = null;
  previewEvents: ClockEvent[] = [];

  /**
   * When set (via MAT_DIALOG_DATA `forceUsername`) the dropdown is locked
   * to a single entry — used by the badge-scan flow where the cashier has
   * already proven who they are by physically scanning the badge, so we
   * shouldn't let them silently punch as somebody else. The picker is
   * still rendered (so the user can confirm the name) but disabled.
   */
  /** Print timesheet on clockout — only visible when a receipt printer is attached. */
  printTimesheetOnClockout = false;
  hasReceiptPrinter = false;

  forcedUsername: string | null = null;
  private scanSub: Subscription;

  constructor(
    private dialogRef: MatDialogRef<DeviceModeUnlockPopupComponent>,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private utilsService: UtilsService,
    private clockEventService: ClockEventService,
    private barcodeScanService: BarcodeScanService,
    private networkPrinterService: NetworkPrinterService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    if (data?.mode === 'lock-screen') { this.mode = 'lock-screen'; }
    else if (data?.mode === 'time-clock') { this.mode = 'time-clock'; }
    if (data?.action === 'clockOut') {
      this.clockAction = 'clockOut';
      this.requestedAction = 'clockOut';
    }
    if (this.mode === 'lock-screen') { this.dialogRef.disableClose = true; }
    if (typeof data?.forceUsername === 'string' && data.forceUsername.trim()) {
      this.forcedUsername = data.forceUsername.trim();
    }
  }

  ngOnDestroy(): void {
    if (this.scanSub) { this.scanSub.unsubscribe(); }
  }

  ngOnInit(): void {
    // Hardware badge scanner: auto-fill username on EMP:<userId> scan
    if (this.mode === 'time-clock') {
      this.scanSub = this.barcodeScanService.scanned$.subscribe(code => {
        const match = /^EMP:([\w-]+)$/i.exec(code);
        if (!match) { return; }
        this.userService.getById(match[1]).subscribe((user: any) => {
          if (user?.username) {
            this.forcedUsername = user.username;
            this.selectedUsername = user.username;
            this.employees = [{
              username: user.username,
              displayName: this.formatName(user),
              role: ((user as any).role || [])[0] || '',
            }];
          }
        });
      });
    }

    // Check for a receipt printer to enable the "print timesheet" checkbox.
    if (this.mode === 'time-clock') {
      this.networkPrinterService.getAll().subscribe(
        (printers: any[]) => {
          this.hasReceiptPrinter = Array.isArray(printers)
            && printers.some((p: any) => p.printerType === 'receipt');
        },
        () => { /* ignore — checkbox just won't show */ },
      );
    }

    const currentUser: User | null = this.utilsService.getCurrentUser();
    if (currentUser?.username) {
      // Pre-populate with the current logged-in user. Even if employee fetch
      // fails (offline / role-restricted), this lets the operator unlock as
      // themselves — the prior dialog left `username` blank when `email` was
      // not set, which surfaced as "no account on this device".
      this.selectedUsername = currentUser.username;
      this.employees = [{
        username: currentUser.username,
        displayName: this.formatName(currentUser),
        role: currentUser.selectedRole || (currentUser.role && currentUser.role[0]) || '',
      }];
    }

    if (this.forcedUsername) {
      // Lock the picker to the forced entry. We still rely on the org fetch
      // below to populate the displayName; on fetch failure we render the
      // bare username as a single-row list, which is always good enough.
      this.selectedUsername = this.forcedUsername;
      this.employees = [{
        username: this.forcedUsername,
        displayName: this.forcedUsername,
        role: '',
      }];
    }

    const orgUuid = currentUser?.orgUuid;
    if (!orgUuid) { return; }
    this.loadingEmployees = true;
    this.userService.getAll(orgUuid).subscribe(
      (users: User[]) => {
        this.loadingEmployees = false;
        if (!Array.isArray(users)) { return; }
        if (this.forcedUsername) {
          // Resolve the forced entry to a real user record so the dialog
          // shows their display name, but keep the dropdown locked to that
          // single row. If we can't find them in the org list, fall through
          // to the bare-username placeholder we set above.
          const match = users.find(u => u?.username === this.forcedUsername);
          if (match) {
            const roles: string[] = (match as any).role || [];
            this.employees = [{
              username: match.username,
              displayName: this.formatName(match),
              role: this.preferredRoleLabel(roles),
            }];
          }
          this.selectedUsername = this.forcedUsername;
          return;
        }
        const list: UnlockEmployee[] = users
          .filter(u => !!u?.username)
          .filter(u => this.isUserSelectableForMode(u))
          .map(u => {
            const raw = (u as any).role;
            const roles: string[] = Array.isArray(raw) ? raw : (typeof raw === 'string' ? [raw] : []);
            const role = this.preferredRoleLabel(roles);
            return {username: u.username, displayName: this.formatName(u), role};
          })
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
        // Ensure the current user is always in the list — they may be
        // absent from getAll results if their orgUuid doesn't match the
        // query (e.g. org-owner accounts created before orgUuid was
        // consistently set).
        if (currentUser?.username && this.isUserSelectableForMode(currentUser)) {
          if (!list.some(e => e.username === currentUser.username)) {
            const raw = (currentUser as any).role;
            const roles: string[] = Array.isArray(raw) ? raw : (typeof raw === 'string' ? [raw] : []);
            list.unshift({
              username: currentUser.username,
              displayName: this.formatName(currentUser),
              role: this.preferredRoleLabel(roles),
            });
          }
        }
        // Keep current user at the top; preserve the original selection.
        if (currentUser?.username) {
          const idx = list.findIndex(e => e.username === currentUser.username);
          if (idx >= 0) {
            const [me] = list.splice(idx, 1);
            list.unshift(me);
          }
        }
        if (list.length > 0) {
          this.employees = list;
          if (!this.employees.some(e => e.username === this.selectedUsername)) {
            this.selectedUsername = this.employees[0].username;
          }
        }
      },
      () => {
        this.loadingEmployees = false;
        // Fall back to single-user mode (already pre-populated above).
      },
    );
  }

  /**
   * Template hook — true when the badge-scan flow forced a specific
   * employee. The dropdown should render disabled in that case.
   */
  get isUsernameLocked(): boolean {
    return !!this.forcedUsername;
  }

  /**
   * unlock / lock-screen restrict to roles that can dismiss the lock —
   * Admin / Manager / Server / Cashier. time-clock is intentionally
   * permissive: every employee on the org clocks in here, including
   * roles like Kiosk that should never unlock the register.
   *
   * Cashier is included so the closeout (Batch) re-auth dialog — which
   * reuses this picker in `unlock` mode — lets a logged-in cashier
   * confirm their own credentials. Without it the cashier was filtered
   * out of their own register's closeout flow and the dialog defaulted
   * to an admin row they had no password for.
   */
  private isUserSelectableForMode(u: User | any): boolean {
    const raw = (u as any).role;
    const roles: string[] = Array.isArray(raw) ? raw : (typeof raw === 'string' ? [raw] : []);
    // NoAccess users still appear in the time-clock picker (they need to
    // clock in / out) but never in unlock / lock-screen — they cannot
    // resume a session. Backend `authenticate()` rejects them anyway,
    // but hiding them here avoids the confusing "incorrect" error the
    // operator would see after typing a correct PIN for a NoAccess user.
    if (this.mode === 'time-clock') { return true; }
    if (roles.includes(Constants.NO_ACCESS)) { return false; }
    const unlockable = new Set<string>([
      Constants.ADMIN, Constants.MANAGER, Constants.SERVER, Constants.CASHIER,
    ]);
    return roles.some(r => unlockable.has(r));
  }

  private preferredRoleLabel(roles: string[]): string {
    if (this.mode === 'time-clock') { return roles[0] || ''; }
    const unlockable = new Set<string>([
      Constants.ADMIN, Constants.MANAGER, Constants.SERVER, Constants.CASHIER,
    ]);
    return roles.find(r => unlockable.has(r)) || roles[0] || '';
  }

  private formatName(user: User | UnlockEmployee | any): string {
    if (!user) { return ''; }
    const first = (user.firstName || '').trim();
    const last = (user.lastName || '').trim();
    const full = (first + ' ' + last).trim();
    if (full) { return full; }
    return user.username || '';
  }

  /** Title shown in the dialog header. */
  get headerTitle(): string {
    if (this.mode === 'lock-screen') { return 'Locked — sign in to continue'; }
    if (this.mode === 'time-clock') {
      return this.clockAction === 'clockOut' ? 'End your shift' : 'Start your shift';
    }
    return 'Unlock device';
  }

  get headerSubtitle(): string {
    if (this.mode === 'lock-screen') {
      return 'Choose your account and enter your PIN or password to resume.';
    }
    if (this.mode === 'time-clock') {
      const action = this.clockAction === 'clockOut' ? 'clock out' : 'clock in';
      return `Choose your name and enter your PIN or password to ${action}.`;
    }
    return 'Choose your account and enter your PIN or password to unlock.';
  }

  get credentialPlaceholder(): string {
    return 'PIN or password';
  }

  get submitButtonLabel(): string {
    if (this.mode === 'lock-screen') { return 'Sign in'; }
    if (this.mode === 'time-clock') {
      return this.clockAction === 'clockOut' ? 'Clock Out' : 'Clock In';
    }
    return 'Unlock';
  }

  /**
   * Confirm-step header — always action-specific so the cashier sees
   * exactly what's about to happen. Replaces the previously generic
   * "Confirm punch" wording, which let users confirm the WRONG action
   * when preview silently flipped `clockAction` away from their menu
   * intent (the user-facing complaint that opened this fix).
   */
  get confirmHeaderTitle(): string {
    const who = this.previewName || this.selectedUsername || 'employee';
    return this.clockAction === 'clockOut'
      ? `Clock OUT ${who}?`
      : `Clock IN ${who}?`;
  }

  /** Confirm-step pill describing the upcoming write — distinct from the
   *  "currently on shift" status pill which describes existing state. */
  get confirmActionBannerText(): string {
    return this.clockAction === 'clockOut'
      ? 'Will end the current shift'
      : 'Will start a new shift';
  }

  get confirmActionBannerKind(): 'clock-in' | 'clock-out' {
    return this.clockAction === 'clockOut' ? 'clock-out' : 'clock-in';
  }

  /**
   * True when preview flipped `clockAction` to the opposite of what the
   * cashier originally requested from the menu. Example: cashier hit
   * "Start Shift" but the selected employee was already on shift; we
   * snap to clock-out (the only sensible commit) AND surface this
   * warning so the cashier explicitly sees the change before pressing
   * the button.
   */
  get actionFlippedByPreview(): boolean {
    return this.confirmStep && this.clockAction !== this.requestedAction;
  }

  get intentMismatchWarning(): string {
    if (!this.actionFlippedByPreview) { return ''; }
    const who = this.previewName || this.selectedUsername || 'This employee';
    if (this.requestedAction === 'clockIn' && this.clockAction === 'clockOut') {
      return `${who} is already on shift. The button below will clock them OUT.`;
    }
    if (this.requestedAction === 'clockOut' && this.clockAction === 'clockIn') {
      return `${who} is not currently on shift. The button below will clock them IN.`;
    }
    return '';
  }

  close(): void {
    if (this.mode === 'lock-screen') { return; }
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.password) {
      this.errorMessage = this.mode === 'time-clock'
        ? 'Enter your PIN or password to continue.'
        : 'Enter the account password to continue.';
      return;
    }
    if (!this.selectedUsername) {
      this.errorMessage = 'Choose an employee to authenticate as.';
      return;
    }
    this.loading = true;
    this.errorMessage = '';

    if (this.mode === 'time-clock') {
      this.submitTimeClockPreview();
      return;
    }

    this.authenticationService.login(this.selectedUsername, this.password).subscribe(
      (response: any) => {
        this.loading = false;
        if (!response?.user) {
          this.errorMessage = 'Incorrect password.';
          this.password = '';
          return;
        }
        this.dialogRef.close({verified: true, username: this.selectedUsername});
      },
      () => {
        this.loading = false;
        this.errorMessage = 'Incorrect password.';
        this.password = '';
      },
    );
  }

  /**
   * Step 1 of the time-clock flow — verify credentials with /timeclock/preview
   * and flip into the confirmation screen. No clock event is written.
   * Sends both `pin` and `password` (BE tries PIN first, then password —
   * DineNGo-backend/_helpers/employeeCredentials.js).
   */
  private submitTimeClockPreview(): void {
    const credential = this.password;
    const looksLikePin = /^\d{4,8}$/.test(credential);
    const payload: any = {
      username: this.selectedUsername,
      ...(looksLikePin ? {pin: credential} : {}),
      password: credential,
    };
    this.clockEventService.previewPunch(payload).subscribe(
      (resp: PreviewPunchResponse) => {
        this.loading = false;
        if (!resp || !resp.success) {
          this.errorMessage = 'Could not verify your credentials. Try again.';
          this.password = '';
          return;
        }
        this.previewName = this.formatName(resp.user);
        this.previewOpenShift = resp.openShift;
        this.previewEvents = Array.isArray(resp.recentEvents) ? resp.recentEvents : [];
        // Snap the action to whatever's actually appropriate given the
        // shift state — if they're already on shift, the only sensible
        // commit is Clock Out, and vice-versa. Stops the cashier from
        // trying to clock in someone who's already working.
        this.clockAction = this.previewOpenShift ? 'clockOut' : 'clockIn';
        this.confirmStep = true;
      },
      (err: any) => {
        this.loading = false;
        const status = err?.status;
        const message = err?.error?.message || err?.error || err?.message || '';
        if (status === 401 || /pin|password|credential|inactive/i.test(String(message))) {
          this.errorMessage = 'Incorrect PIN or password.';
          this.password = '';
          return;
        }
        this.errorMessage = typeof message === 'string' && message
          ? message
          : 'Could not verify your credentials. Try again.';
      },
    );
  }

  /** Back from confirmation to the credential entry screen. */
  cancelConfirm(): void {
    this.confirmStep = false;
    this.previewOpenShift = null;
    this.previewEvents = [];
    this.previewName = '';
    this.password = '';
    this.errorMessage = '';
  }

  /**
   * Step 2 — the cashier hit the explicit Clock In/Out button on the
   * confirmation screen. This is where we actually commit the punch.
   */
  confirmPunch(): void {
    if (!this.confirmStep || this.loading) { return; }
    this.loading = true;
    this.errorMessage = '';
    this.commitTimeClock();
  }

  /**
   * Picks the right /timeclock endpoint based on clockAction, sends both
   * `pin` and `password` again (the BE doesn't keep state between the
   * preview and the commit — it re-verifies on every write).
   */
  private commitTimeClock(): void {
    const credential = this.password;
    const looksLikePin = /^\d{4,8}$/.test(credential);
    const payload: any = {
      username: this.selectedUsername,
      ...(looksLikePin ? {pin: credential} : {}),
      password: credential,
    };

    const handleSuccess = (response: ClockInResponse | ClockOutResponse) => {
      this.loading = false;
      if (!response?.success) {
        this.errorMessage = 'Could not record the punch. Try again.';
        return;
      }
      this.dialogRef.close({
        verified: true,
        username: this.selectedUsername,
        action: this.clockAction,
        shift: response.shift,
        alreadyOpen: (response as ClockInResponse).alreadyOpen,
        workedSeconds: (response as ClockOutResponse).workedSeconds,
        printTimesheet: this.clockAction === 'clockOut' && this.printTimesheetOnClockout,
        previewEvents: this.previewEvents,
        previewName: this.previewName,
      });
    };

    const handleError = (err: any) => {
      this.loading = false;
      // Offline outbox enqueue path — ClockEventService rethrows a
      // synthetic {success:false, queued:true} when the request couldn't
      // reach the server. Treat it as a soft success: close the dialog
      // with queued:true so the caller can toast "will sync when online"
      // instead of showing a failure.
      if (err && err.queued === true) {
        this.dialogRef.close({
          verified: true,
          queued: true,
          username: this.selectedUsername,
          action: this.clockAction,
        });
        return;
      }
      const status = err?.status;
      const message = err?.error?.message || err?.error || err?.message || '';
      if (status === 401 || /pin|password|credential|inactive/i.test(String(message))) {
        this.errorMessage = 'Incorrect PIN or password.';
        this.password = '';
        return;
      }
      if (status === 409 && this.clockAction === 'clockOut') {
        this.errorMessage = 'You are not currently on shift.';
        return;
      }
      this.errorMessage = typeof message === 'string' && message
        ? message
        : 'Could not record the punch. Try again.';
    };

    const stream: Observable<ClockInResponse | ClockOutResponse> =
      this.clockAction === 'clockOut'
        ? this.clockEventService.clockOut(payload)
        : this.clockEventService.clockIn(payload);
    stream.subscribe(handleSuccess, handleError);
  }
}
